import axios from 'axios'
import { computed, createApp, defineAsyncComponent, reactive, ref } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import { isCheckoutChunkLoadError, promptCheckoutReload } from './helpers/checkoutChunkRecovery'
import { shouldDropPageLoadNetworkError } from './helpers/sentryPageLoadNetworkFilter.js'
import { createPinia } from 'pinia'
import { MotionPlugin } from '@vueuse/motion'
import { port, apiDomain, useDevAPI, algoliaAppId, algoliaSearchKey } from '../../config/config/settings'
import { useStore } from './composables/store'
import VueClipboard from 'vue3-clipboard'
import Vue3TouchEvents from 'vue3-touch-events'
import App from './App.vue'
import './index.css'
import './styles/places-autocomplete.css'

/*
    If these are dynamicly imported I end up with a blank
    screen while the route is waiting for the component
    to download... not sure how to fix.
*/

String.prototype.toTitleCase = function () {
	return this.replace(/\w\S*/g, function (txt) {
		return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
	})
}

import SignUp from './components/pages/sign-up.vue'
import SignUpError from './components/pages/sign-up-error.vue'
import Welcome from './components/pages/welcome.vue'
import Login from './components/layout/login.vue'

import Home from './components/pages/home.vue'
import AboutUs from './components/pages/about-us-new.vue'
import SignIn from './components/pages/sign-in.vue'
import ItemID from './components/pages/item.vue'
import Product from './components/pages/product.vue'
const Checkout = defineAsyncComponent({
	loader: () => import('./components/checkout/checkout.vue'),
	onError(error, retry, fail, attempts) {
		if (isCheckoutChunkLoadError(error) && attempts <= 1) {
			retry()
			return
		}
		if (isCheckoutChunkLoadError(error)) {
			promptCheckoutReload()
		}
		fail()
	},
})
import Cart from './components/pages/ihh-items/cart.vue'
// CartCopy import removed as it was unused
import OrderConfirmation from './components/pages/order-confirmation.vue'
import TermsConditions from './components/pages/terms-conditions.vue'
import HelpCenter from './components/pages/help-center.vue'
import FinalSaleItems from './components/pages/final-sale-items.vue'
import ReturnsPortal from './components/pages/returns-new.vue'
import Brands from './components/pages/brands.vue'
import Midwife from './components/pages/midwife-birth-kits.vue'
import CustomBirthKitService from './components/pages/custom-birth-kit-service.vue'
import Shipping from './components/pages/shipping-info.vue'
import Lost from './components/pages/lost.vue'
import DiscountTerms from './components/pages/discount-code-terms.vue'
import SiteMap from './components/pages/sitemap.vue'
import SitemapBrands from './components/pages/sitemap-brands.vue'
import SitemapCategories from './components/pages/sitemap-categories.vue'
import PrivacyPolicy from './components/pages/privacy-policy.vue'
import SMS from './components/pages/sms.vue'
import BirthPoolSetup from './components/pages/birth-pool-setup.vue'
import WaterBirthPackage from './components/pages/water-birth-package.vue'
import NewmanDopplerTradeInOffer from './components/pages/newman-doppler-trade-in-offer.vue'
import GiftCertificates from './components/pages/gift-certificates.vue'
import BirthKit from './components/pages/birth-kit.vue'
import BirthKitBuilder from './components/pages/birth-kit-builder.vue'
import BirthKitOrderForm from './components/pages/birth-kit-order-form.vue'
import CustomBirthKitUpdates from './components/pages/custom-birth-kit-updates.vue'
import CustomBirthKitBlogPost from './components/pages/custom-birth-kit-blog-post.vue'
import CmsPage from './components/pages/cms-page.vue'
import Search from './components/pages/search.vue'
import { storefrontRoutes } from './storefrontRoutes.js'
import { bindStorefrontRoutes } from './bindStorefrontRoutes.js'
import FAQS from './components/pages/ihh-items/faqs.vue'
import TermsAndConditions from './components/pages/ihh-items/terms-and-conditions.vue'

const Category = () => import('./components/pages/category.vue')
import Brand from './components/pages/brand.vue'

const isDev = process.env.NODE_ENV != 'production'

const Account = () => import('./components/pages/account.vue')
const accountAddresses = () => import('./components/account/addresses.vue')
const accountOrders = () => import('./components/account/orders.vue')
const accountOrderDetails = () => import('./components/account/order-details.vue')
const accountRecentlyViewed = () => import('./components/account/recently-viewed.vue')
const accountRecommended = () => import('./components/account/recommended.vue')
const accountSettings = () => import('./components/account/settings.vue')
const accountWishlists = () => import('./components/account/wishlists.vue')
const accountBirthKits = () => import('./components/account/birth-kits.vue')
const accountClaims = () => import('./components/account/claims.vue')
const accountApprovals = () => import('./components/account/approvals.vue')
const accountWishlistForm = () => import('./components/account/wishlist-form.vue')
const sharedWishlist = () => import('./components/pages/shared-wishlists.vue')
const invoiceTemplate = () => import('./components/account/order-invoice.vue')
const DevTools = () => import('./components/pages/dev.vue')

const routes = bindStorefrontRoutes(storefrontRoutes, {
	Home,
	AboutUs,
	SignUpError,
	Login,
	Welcome,
	Account,
	accountOrders,
	accountAddresses,
	accountOrderDetails,
	accountRecentlyViewed,
	accountRecommended,
	accountSettings,
	accountBirthKits,
	accountClaims,
	accountApprovals,
	accountWishlists,
	accountWishlistForm,
	sharedWishlist,
	invoiceTemplate,
	Product,
	ItemID,
	Cart,
	Checkout,
	OrderConfirmation,
	TermsConditions,
	HelpCenter,
	Category,
	Brand,
	BirthKit,
	BirthKitBuilder,
	BirthKitOrderForm,
	CustomBirthKitUpdates,
	CustomBirthKitBlogPost,
	ReturnsPortal,
	FinalSaleItems,
	Brands,
	Midwife,
	CustomBirthKitService,
	Shipping,
	Lost,
	FAQS,
	TermsAndConditions,
	DiscountTerms,
	SiteMap,
	SitemapBrands,
	SitemapCategories,
	PrivacyPolicy,
	SMS,
	BirthPoolSetup,
	WaterBirthPackage,
	NewmanDopplerTradeInOffer,
	GiftCertificates,
	Search,
	DevTools,
	CmsPage,
})

const router = createRouter({
	history: createWebHistory(),
	scrollBehavior(to, from, savedPosition) {
		let toPath = to.matched?.[0]?.path

		let sameComponent = to.matched?.[0]?.components?.default == from.matched?.[0]?.components?.default
		// The catch-all is one component for every admin-created page, so still scroll to the top.
		let sameComponentExceptions = ['/inventory/:id', '/products/:id', /account\/*/g, /^\/:pathMatch/]
		let exceptionExists = sameComponentExceptions.some((e) => !!toPath?.match(e)?.length)

		if (to.hash) {
			return { el: to.hash, behavior: 'smooth' }
		}
		if (savedPosition) return savedPosition
		if (sameComponent && !exceptionExists) return null

		return { top: 0 }
	},
	routes,
})

router.onError((error, to) => {
	if (to?.path?.startsWith('/checkout') && isCheckoutChunkLoadError(error)) {
		promptCheckoutReload()
	}
})

if (typeof window !== 'undefined') {
	window.addEventListener('unhandledrejection', (event) => {
		const reason = event.reason
		if (!window.location.pathname.startsWith('/checkout')) return
		if (!isCheckoutChunkLoadError(reason)) return
		event.preventDefault()
		promptCheckoutReload()
	})
}

import { vSafeHtml } from './helpers/safeHtml.js'
import { startHomePage } from './helpers/homePage'

const app = createApp(App)
app.directive('safe-html', vSafeHtml)
const pinia = createPinia()

app.use(router)
app.use(pinia)
app.use(Vue3TouchEvents)
app.use(MotionPlugin)
app.use(VueClipboard, {
	autoSetContainer: true,
	appendToBody: true,
})

import * as Sentry from '@sentry/vue'
import { shouldDropSentryEvent } from './helpers/sentry.js'

function apiTracePropagationTarget(apiBase) {
	try {
		const url = new URL(apiBase)
		const host = url.host.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
		return new RegExp(`^${url.protocol}//${host}(?:/|$)`)
	} catch {
		return apiBase
	}
}

Sentry.init({
	app,
	dsn: 'https://924fdcd938dfb37858e3b33aaf7aaddf@o4506316278595584.ingest.us.sentry.io/4510796279906304',
	enabled: !isDev,
	ignoreErrors: [
		/Failed to fetch.*pinterest/i,
		'Failed to fetch (ct.pinterest.com)',
		/Load failed.*pinterest/i,
		/Load failed.*analytics\.google/i,
		/Failed to fetch.*analytics\.google/i,
		/Failed to fetch.*doubleclick/i,
		/Failed to fetch.*googleadservices/i,
		/Failed to fetch.*datadoghq/i,
		/Can't find variable: contains/,
		/Load failed.*google\.com/,
		/Could not load "util"/,
		/Unreachable hosts.*algolia/i,
		/Large Render Blocking Asset/,
		/Request aborted/i,
		/Failed to fetch.*babylist\.com/i,
		/AbortError.*paymentrequest/i,
		/Java object is gone/i,
		/zoid destroyed all components/i,
		/Frame closed before tokenization/i,
		/Cannot contact the gateway at this time/i,
		/window\.webkit\.messageHandlers/i,
		/SecurityError.*localStorage/i,
		/The operation is insecure/i,
		/filter settings/i,
		/Request failed with status code 429/,
	],
	denyUrls: [
		/webkit-masked-url:\/\/hidden/, // Safari browser extensions
		/extensions\//i, // Browser extensions
		/^chrome:\/\//i, // Chrome extensions
		/^moz-extension:\/\//i, // Firefox extensions
		/maps\/api\/js/, // Google Maps API
		/ct\.pinterest\.com/, // Pinterest tracking
		/gtag\/js/, // Google Analytics/Tag Manager
		/\/scripts\/pusher\.js/, // Third-party pusher script
	],
	environment: isDev ? 'development' : 'production',
	integrations: [
		Sentry.browserTracingIntegration({ router }),
		Sentry.replayIntegration({
			maskAllText: true,
			maskAllInputs: true,
			blockAllMedia: true,
		}),
	],
	maxBreadcrumbs: 150,
	release: __SENTRY_RELEASE__ || undefined,
	tracesSampleRate: isDev ? 1.0 : 0.2,
	// Propagate traces to the API origin the storefront actually calls (settings apiDomain), plus localhost in dev.
	tracePropagationTargets: ['localhost', apiTracePropagationTarget(apiDomain)],
	replaysSessionSampleRate: 0, // Included plan is 50 session replays a month. Record only sessions that hit an error so we do not pay for overage.
	replaysOnErrorSampleRate: 1.0,
	// Explicit setCheckoutUser still attaches id/email. This only turns off automatic IP/header capture.
	sendDefaultPii: false,
	beforeBreadcrumb(breadcrumb) {
		// Drop chatty keystroke-level console breadcrumbs that bloat the envelope.
		// These fire on every character typed into the email input and provide no diagnostic value.
		if (breadcrumb.category === 'console') {
			const msg = breadcrumb.message || ''
			if (msg.includes('checkout-login watcher fired') || msg.includes('sessionStorage guest after update')) {
				return null
			}
		}
		return breadcrumb
	},
	beforeSend(event, hint) {
		if (isDev) {
			console.log('[Sentry] Event captured:', event)
		}

		const message = event.message || event.exception?.values?.[0]?.value || ''

		const droppedPatterns = [
			/Failed to fetch.*google\.com/i,
			/Failed to fetch \(www\.google\.com\)/i,
			/error loading dynamically imported module/i,
			/Unable to preload CSS/i,
			/has insufficient stock/i,
			/can only purchase a maximum/i,
		]

		for (const pattern of droppedPatterns) {
			if (pattern.test(message)) return null
		}

		// Offline / backgrounded page loads. Payment, checkout, and mutations stay.
		if (shouldDropPageLoadNetworkError(event, hint)) return null

		if (shouldDropSentryEvent(event)) return null

		return event
	},
})

/**
 * Algolia Search Insights
 */
import aa from 'search-insights'
aa('init', {
	apiKey: algoliaSearchKey,
	appId: algoliaAppId,
	useCookie: true,
})

/*

Global Properties

*/

import breakpoints from './helpers/breakpoints'
import darkMode from './helpers/darkMode'
import formatPhone from './helpers/formatPhone'
import { initUser } from './helpers/user'
import initCart from './helpers/cart'
import saveCart from './helpers/cart'
import { loadMemberForCartInit } from './helpers/cartInitAuth.js'
import { clearSession, syncAuthHeader, installSessionResumeListeners } from './helpers/session'

app.provide('breakpoints', breakpoints().breakpoints)
app.provide('darkMode', darkMode().darkMode)

let api = useDevAPI ? `http://localhost:${port}` : apiDomain
if (!isDev) api = apiDomain

let editingKitId = window.localStorage.getItem('editingKitId')

const store = useStore()

store.initUserCart = async () => {
	console.log('Initializing user cart...')
	// A rejected member session (401/403) is an expired or revoked login, not a
	// broken cart. Clear it and let initCart build the guest cart. Other errors
	// still reject so the caller can report them.
	const loaded = await loadMemberForCartInit({
		loadUser: () => initUser(),
		clearSession: () => clearSession(store, axios),
	})
	if (!loaded.clearedStaleSession) store.user = loaded.user
	let cart = await initCart(store.user, api)
	store.cart = cart.cart
	store.itemsCount = cart.itemsCount
	store.certificatesCount = cart.certificatesCount
	store.cartTotalItems = cart.cartTotalItems
	store.cartSubtotal = cart.cartSubtotal
	store.texasStateSalesTax = cart.texasStateSalesTax
	store.originalCart = cart.originalCart
	store.cartTotal = cart.cartTotal

	store.saveCart = async () => {
		await cart.saveCart()
	}
	store.syncOriginalCart = () => {
		cart.syncOriginalCart()
	}
}

store.userReady = store.initUserCart()
	.then(() => {
		store.cartInitialized = true
	})
	.catch((error) => {
		console.error('Failed to initialize user cart:', error)
		Sentry.captureException(error)
		store.cartInitialized = true
	})

// store.saveCart = async () => {
// 	await saveCart()
// }

syncAuthHeader(axios)

installSessionResumeListeners({ store, axios })

app.provide('store', store)
app.provide('axios', axios)

app.provide('mode', import.meta.env.MODE)
app.provide('formatPhone', formatPhone)

/*

Global Components

*/

import Mango from './helpers/Mango.vue'
import Spinner from './components/layout/spinner.vue'
import Modal from './components/layout/modal.vue'
import Button from './components/partials/button.vue'
// HerbalNatural import removed as it was unused

app.component('Mango', Mango)
app.component('Spinner', Spinner)
app.component('Modal', Modal)
app.component('btn', Button)
// app.component('resource', defineAsyncComponent(Layouter.vue')))

/*

Route Guard

*/

router.beforeEach(async (to, from, next) => {
	// Block access to BK edit pages if user is not logged in
	if (!store.user?.id && store.userReady && (to.meta?.protected || /\/birthKits\/(.+)\/edit/.test(to.path))) {
		try {
			await store.userReady
		} catch {
			// init already recorded the failure; the checks below decide access
		}
	}

	if (to.path.match(/\/birthKits\/(.+)\/edit/)) {
		if (!store.user?.id) {
			return next(to.path.replace('/edit', ''))
		}
	}

	if (to.meta?.protected && !store.user?.id) {
		console.log('protected', store.user?.id)
		store.login = { next: to.path }
		if (to.meta?.fallback) {
			console.log('to', to)
			// Get params from path and add them to the fallback path (/event/:id)
			let fallback = to.meta.fallback
				?.split('/')
				?.map((p) => {
					if (p.includes(':')) {
						let param = p.replaceAll(':', '')
						return to.params[param] || ''
					} else {
						return p
					}
				})
				?.join('/')
			return next(fallback || to.meta.fallback)
		}
		return next('')
	}

	if (to.meta?.adminOnly && !store.user?.roles?.includes('admin')) {
		console.log('adminOnly route blocked for non-admin user')
		return next('/')
	}

	if (to.meta?.title) {
		document.title = to.meta.title
	}

	// Google Analytics manual page_view event
	if (typeof window.gtag === 'function') {
		window.gtag('event', 'page_view', {
			page_title: to.meta?.title || document.title,
			page_location: window.location.origin + to.fullPath,
			send_to: 'G-SLGVZKFN0K', // Target GA property
		})
	}
	next()
})

// Keep the home page data the server sent, or fetch it in the background, so the
// home page renders complete however it is reached (helpers/homePage.js).
startHomePage()

const mountEl = document.querySelector('#app')
if (mountEl) {
	app.mount(mountEl)
}
